import PropTypes from 'prop-types';
import { noCase } from 'change-case';
import { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { Link as RouterLink } from 'react-router-dom';
// @mui
import { alpha, styled } from '@mui/material/styles';
import {
  Box,
  List,
  Badge,
  Button,
  Tooltip,
  Divider,
  Typography,
  ListItemText,
  ListSubheader,
  ListItemAvatar,
  ListItemButton,
  Avatar,
  Stack,
} from '@mui/material';
// routes
import { PATH_DASHBOARD } from '../../../routes/paths';
// components
import Iconify from '../../../components/Iconify';
import Scrollbar from '../../../components/Scrollbar';
import MenuPopover from '../../../components/MenuPopover';
import { IconButtonAnimate } from '../../../components/animate';
import axios from '../../../utils/axios';

// ----------------------------------------------------------------------

const BellIconStyle = styled(Box)(({ theme, open }) => ({
  width: 44,
  height: 44,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '12px',
  transition: 'all 0.3s ease',
  backgroundColor: open ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
  color: open ? theme.palette.primary.main : theme.palette.text.secondary,
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    color: theme.palette.primary.main,
  },
}));

const NotificationItemStyle = styled(ListItemButton)(({ theme, unread }) => ({
  padding: theme.spacing(2, 2.5),
  borderRadius: '12px',
  margin: theme.spacing(0, 1, 1, 1),
  transition: 'all 0.2s ease',
  ...(unread && {
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
    },
  }),
  '&:hover': {
    backgroundColor: alpha(theme.palette.grey[500], 0.04),
  },
}));

// ----------------------------------------------------------------------

export default function NotificationsPopover() {
  const { enqueueSnackbar } = useSnackbar();
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(null);
  const AccessToken = window.localStorage.getItem('accessToken');

  const totalUnRead = (Array.isArray(notifications) ? notifications : []).filter((item) => item?.status === 0).length;

  const handleOpen = (event) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  useEffect(() => {
    axios.get(`/api/system/notification/user/${AccessToken}/request/user`).then(data => {
      setNotifications(data.data.notif || []);
    }).catch(err => console.error('Failed to fetch notifications', err));
  }, [AccessToken]);

  const handleMarkAllAsRead = async () => {
    try {
      await axios(`/api/clear/notification/clear/all/${AccessToken}/by/user`);
      setNotifications(
        notifications?.map((notification) => ({
          ...notification,
          status: 1,
        }))
      );
      enqueueSnackbar('Notifications cleared successfully');
    } catch (err) {
      enqueueSnackbar('Failed to clear notifications', { variant: 'error' });
    }
  };

  return (
    <>
      <IconButtonAnimate onClick={handleOpen} sx={{ p: 0 }}>
        <BellIconStyle open={Boolean(open)}>
          <Badge 
            badgeContent={totalUnRead} 
            color="error"
            sx={{
              '& .MuiBadge-badge': {
                fontWeight: 800,
                fontSize: '0.65rem',
                height: 18,
                minWidth: 18,
                boxShadow: (theme) => `0 0 0 2px ${theme.palette.background.paper}`,
              }
            }}
          >
            <Iconify icon={totalUnRead > 0 ? "eva:bell-fill" : "eva:bell-outline"} width={24} height={24} />
          </Badge>
        </BellIconStyle>
      </IconButtonAnimate>

      <MenuPopover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        sx={{ width: 380, p: 0, mt: 1.5, ml: 0.75, borderRadius: '20px' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', py: 2.5, px: 3 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>Notifications</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              {totalUnRead > 0 ? `You have ${totalUnRead} unread alerts` : 'All caught up!'}
            </Typography>
          </Box>

          {totalUnRead > 0 && (
            <Tooltip title="Mark all as read">
              <IconButtonAnimate 
                sx={{ 
                  color: 'primary.main', 
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                  '&:hover': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.2) }
                }} 
                onClick={handleMarkAllAsRead}
              >
                <Iconify icon="eva:done-all-fill" width={20} height={20} />
              </IconButtonAnimate>
            </Tooltip>
          )}
        </Box>

        <Divider sx={{ borderStyle: 'dashed', mb: 1 }} />

        <Scrollbar sx={{ maxHeight: 440 }}>
          <List disablePadding>
             {notifications.length > 0 ? (
               notifications.map((notification) => (
                 <NotificationItem key={notification.id} notification={notification} />
               ))
             ) : (
               <Box sx={{ py: 8, textAlign: 'center', opacity: 0.5 }}>
                 <Iconify icon="eva:bell-off-outline" width={48} height={48} sx={{ mb: 1, mx: 'auto' }} />
                 <Typography variant="body2">No notifications yet</Typography>
               </Box>
             )}
          </List>
        </Scrollbar>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Box sx={{ p: 2 }}>
          <Button 
            fullWidth 
            variant="soft"
            component={RouterLink}
            to={PATH_DASHBOARD.general.notif}
            onClick={handleClose}
            sx={{ 
              borderRadius: '12px', 
              fontWeight: 800,
              py: 1.5,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
              color: 'primary.main',
              '&:hover': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12) }
            }}
          >
            View All History
          </Button>
        </Box>
      </MenuPopover>
    </>
  );
}

// ----------------------------------------------------------------------

function NotificationItem({ notification }) {
  const isUnread = notification.status === 0;

  return (
    <NotificationItemStyle unread={isUnread}>
      <ListItemAvatar>
        <Avatar 
          sx={{ 
            bgcolor: (theme) => isUnread ? 'primary.main' : alpha(theme.palette.grey[500], 0.1),
            color: (theme) => isUnread ? 'common.white' : theme.palette.text.secondary,
            width: 40,
            height: 40,
            borderRadius: '12px'
          }}
        >
          <Iconify icon={isUnread ? "eva:bell-fill" : "eva:bell-outline"} width={20} height={20} />
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={
          <Typography variant="subtitle2" sx={{ fontWeight: isUnread ? 800 : 600 }}>
            System Alert
          </Typography>
        }
        secondary={
          <Stack spacing={0.5}>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
              {noCase(notification?.message)}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled', display: 'flex', alignItems: 'center' }}>
              <Iconify icon="eva:clock-outline" sx={{ mr: 0.5, width: 14, height: 14 }} />
              {notification?.date}
            </Typography>
          </Stack>
        }
      />
    </NotificationItemStyle>
  );
}
